import React, { useState, useEffect, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { CNY_PROMO, getCNYPromoEndDate } from '../../utils/cnyPromo'
import { stripeCheckoutLink } from '../../redux/actions/myAccountAction'
import { getStripePlan } from '../../redux/actions/myAccountAction'
import { membershipNameType } from '../../utils/constant'
import { formatNumber, formatPercent } from '../../i18n/format'
import './cny-banner.css'

// The CNY prices are whole US dollars: '$600', not '$600.00'.
const wholeUsd = (amount: number) =>
  formatNumber(amount, { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })

const CNYBanner: React.FC = () => {
  const { t } = useTranslation('offers')
  const videoRef = useRef<HTMLVideoElement>(null)
  const [timeLeft, setTimeLeft] = useState(getTimeLeft())
  const [loading, setLoading] = useState(false)
  const [proYearlyPriceId, setProYearlyPriceId] = useState('')

  function getTimeLeft() {
    const diff = getCNYPromoEndDate().getTime() - Date.now()
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 }
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / (1000 * 60)) % 60),
      seconds: Math.floor((diff / 1000) % 60),
    }
  }

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(getTimeLeft()), 1000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    getStripePlan().then((res: any) => {
      if (res?.yearly) {
        const proPlan = res.yearly.find((p: any) => p.value === membershipNameType.Pro)
        if (proPlan?.priceId) setProYearlyPriceId(proPlan.priceId)
      }
    })
  }, [])

  const handleClaim = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (loading || !proYearlyPriceId) return
    setLoading(true)
    try {
      const affiliateId =
        typeof window !== 'undefined' && (window as any).affiliateId
          ? String((window as any).affiliateId)
          : undefined
      const res: any = await stripeCheckoutLink({
        priceId: proYearlyPriceId,
        couponCode: CNY_PROMO.couponCode,
        ...(affiliateId && { affiliateId }),
      })
      if (res?.return_url) {
        window.open(res.return_url, '_self')
      } else if (res?.url) {
        window.open(res.url, '_self')
      }
    } finally {
      setLoading(false)
    }
  }

  const pad = (n: number) => String(n).padStart(2, '0')

  return (
    <div className='cny-banner' onClick={handleClaim}>
      {/* Video background */}
      <video
        ref={videoRef}
        className='cny-banner-video'
        autoPlay
        loop
        muted
        playsInline
        preload='auto'
        onCanPlay={() => {
          console.log('CNY banner video onCanPlay fired')
          videoRef.current?.play().catch((e) => console.error('CNY banner video play failed:', e))
        }}
        onLoadedData={() => {
          console.log('CNY banner video onLoadedData fired')
          videoRef.current?.play().catch((e) => console.error('CNY banner video play failed:', e))
        }}
        onError={(e) => {
          const video = e.currentTarget
          const error = video.error
          console.error('CNY banner video error:', {
            code: error?.code,
            message: error?.message,
            networkState: video.networkState,
            readyState: video.readyState,
            src: video.currentSrc
          })
        }}
      >
        <source src='/images/background_CNY.mp4' type='video/mp4' />
      </video>
      <div className='cny-banner-overlay' />

      <div className='cny-banner-content'>
        <div className='cny-banner-left'>
          <div className='cny-banner-lantern'>🏮</div>
          <div className='cny-banner-text'>
            <div className='cny-banner-eyebrow'>{t('cny.banner.eyebrow')}</div>
            <h5 className='cny-banner-title'>
              <Trans
                t={t}
                i18nKey='cny.banner.title'
                values={{
                  oldPrice: wholeUsd(CNY_PROMO.proMonthlyAnnualized),
                  newPrice: wholeUsd(CNY_PROMO.proYearlyPromoPrice),
                }}
                components={{
                  strike: <span className='cny-banner-price-strike' />,
                  price: <span className='cny-banner-price-new' />,
                }}
              />
            </h5>
            <p className='cny-banner-subtitle'>
              <Trans
                t={t}
                i18nKey='cny.banner.subtitle'
                values={{
                  price: wholeUsd(CNY_PROMO.proMonthlyEquivalent),
                  percent: formatPercent(CNY_PROMO.discountPercent / 100),
                }}
                components={{ strong: <strong /> }}
              />
            </p>
            <p className='cny-banner-subtitle'>{t('common:teacherLanguageNotice')}</p>
          </div>
        </div>

        <div className='cny-banner-right'>
          <div className='cny-banner-countdown'>
            <div className='cny-banner-countdown-unit'>
              <span className='cny-banner-countdown-num'>{pad(timeLeft.days)}</span>
              <span className='cny-banner-countdown-label'>{t('promo.countdown.days', { count: timeLeft.days })}</span>
            </div>
            <span className='cny-banner-countdown-sep'>:</span>
            <div className='cny-banner-countdown-unit'>
              <span className='cny-banner-countdown-num'>{pad(timeLeft.hours)}</span>
              <span className='cny-banner-countdown-label'>{t('promo.countdown.hours', { count: timeLeft.hours })}</span>
            </div>
            <span className='cny-banner-countdown-sep'>:</span>
            <div className='cny-banner-countdown-unit'>
              <span className='cny-banner-countdown-num'>{pad(timeLeft.minutes)}</span>
              <span className='cny-banner-countdown-label'>
                {t('promo.countdown.minutes', { count: timeLeft.minutes })}
              </span>
            </div>
            <span className='cny-banner-countdown-sep'>:</span>
            <div className='cny-banner-countdown-unit'>
              <span className='cny-banner-countdown-num'>{pad(timeLeft.seconds)}</span>
              <span className='cny-banner-countdown-label'>
                {t('promo.countdown.seconds', { count: timeLeft.seconds })}
              </span>
            </div>
          </div>
          <button className='cny-banner-cta' onClick={handleClaim} disabled={loading}>
            {loading ? t('promo.processing') : t('cny.banner.cta')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CNYBanner
